<template>
  <div class="address-book-editor">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="bi bi-journal-text me-2"></i>
          {{ $t('messages.title') }}
        </h5>
        <button 
          type="button" 
          class="btn btn-primary btn-sm" 
          @click="showAddModal = true"
          :disabled="isLoading"
        >
          <i class="bi bi-plus-circle me-1"></i>
          {{ $t('messages.addEntry') }}
        </button>
      </div>
      <div class="card-body">
        <div v-if="isLoading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ $t('messages.loadingSpinner') }}</span>
          </div>
          <p class="mt-2 text-muted">{{ $t('messages.loading') }}</p>
        </div>
        <div v-else-if="errorMessage" class="alert alert-danger" role="alert">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>
          {{ errorMessage }}
          <button type="button" class="btn-close float-end" @click="errorMessage = ''" aria-label="Close"></button>
        </div>
        <div v-else-if="entries.length === 0" class="text-center py-4">
          <i class="bi bi-journal-x display-4 text-muted"></i>
          <p class="mt-2 text-muted">{{ $t('messages.noEntries') }}</p>
          <button 
            type="button" 
            class="btn btn-primary btn-sm" 
            @click="showAddModal = true"
          >
            <i class="bi bi-plus-circle me-1"></i>
            {{ $t('messages.addFirstEntry') }}
          </button>
        </div>
        <div v-else class="list-group">
          <div 
            v-for="entry in entries" 
            :key="entry.id" 
            class="list-group-item"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-grow-1">
                <h6 class="mb-1">{{ entry.name }}</h6>
                <p class="mb-0 text-muted">
                  <i class="bi bi-envelope me-1"></i>
                  {{ entry.email }}
                </p>
              </div>
              <div class="btn-group btn-group-sm" role="group">
                <button 
                  type="button" 
                  class="btn btn-outline-primary" 
                  @click="editEntry(entry)"
                  :disabled="isProcessing"
                  :title="$t('messages.edit')"
                >
                  <i class="bi bi-pencil"></i>
                </button>
                <button 
                  type="button" 
                  class="btn btn-outline-danger" 
                  @click="confirmDelete(entry)"
                  :disabled="isProcessing"
                  :title="$t('messages.delete')"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="entries.length > 0" class="card-footer text-muted">
        <small>
          {{ entries.length }} 
          {{ entries.length === 1 ? $t('messages.entry') : $t('messages.entries') }} 
          {{ $t('messages.total') }}
        </small>
      </div>
    </div>
    <div v-if="showAddModal || editingEntry" class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <form @submit.prevent="saveEntry">
            <div class="modal-header">
              <h5 class="modal-title">
                <i :class="editingEntry ? 'bi-pencil' : 'bi-plus-circle'" class="bi me-2"></i>
                {{ editingEntry ? $t('messages.edit') : $t('messages.add') }} {{ $t('messages.entry') }}
              </h5>
              <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label for="entryName" class="form-label">{{ $t('messages.name') }} {{ $t('messages.required') }}</label>
                <input 
                  v-model="formData.name" 
                  type="text" 
                  class="form-control" 
                  id="entryName" 
                  required
                  :disabled="isProcessing"
                >
              </div>
              <div class="mb-3">
                <label for="entryEmail" class="form-label">{{ $t('messages.email') }} {{ $t('messages.required') }}</label>
                <input 
                  v-model="formData.email" 
                  type="email" 
                  class="form-control" 
                  id="entryEmail" 
                  required
                  :disabled="isProcessing"
                >
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeModal" :disabled="isProcessing">
                {{ $t('messages.cancel') }}
              </button>
              <button type="submit" class="btn btn-primary" :disabled="!isFormValid || isProcessing">
                <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2" role="status"></span>
                {{ editingEntry ? $t('messages.update') : $t('messages.add') }} {{ $t('messages.entry') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <div v-if="deletingEntry" class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-centered modal-sm" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-exclamation-triangle-fill text-warning me-2"></i>
              {{ $t('messages.confirmDeleteTitle') }}
            </h5>
            <button type="button" class="btn-close" @click="deletingEntry = null" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p>{{ $t('messages.confirmDeleteMsg') }}</p>
            <p class="mb-0"><strong>{{ deletingEntry.name }}</strong></p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary btn-sm" @click="deletingEntry = null">
              {{ $t('messages.cancel') }}
            </button>
            <button type="button" class="btn btn-danger btn-sm" @click="deleteEntry" :disabled="isProcessing">
              <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ $t('messages.delete') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showAddModal || editingEntry || deletingEntry" class="modal-backdrop fade show"></div>
  </div>
</template>
<script setup>
import { i18Utils } from "@/visualcrossing-core/i18utils";
</script>
<script>
import "/src/visualcrossing-core/vccommunicator_metadata";
import translations from '@/translations/addressbook.js';
export default {
  i18n: {
      messages: translations,
  },
  name: 'AddressBookEditor',
  props: {
    // Optional: pass initial entries
    initialEntries: {
      type: Array,
      default: () => []
    }
  },
  emits: ['entriesUpdated', 'error'],
  data() {
    return {
      hasLoadedOnce: false,
      entries: [],
      isLoading: false,
      isProcessing: false,
      errorMessage: '',
      showAddModal: false,
      editingEntry: null,
      deletingEntry: null,
      formData: {
        name: '',
        email: ''
      }
    };
  },
  computed: {
    vcCommunicator() {
      return window.vcCommunicator || null;
    },
    isFormValid() {
      return this.formData.name.trim() && this.formData.email.trim() && this.isValidEmail(this.formData.email);
    }
  },
  mounted() {
   const modal = this.$el.closest('.modal');
  if (modal) {
    // Listen for Bootstrap modal show event
    modal.addEventListener('show.bs.modal', () => {
      if (!this.hasLoadedOnce) {
        this.refreshList();
        this.hasLoadedOnce = true;
      }
    });
  }
  },
  beforeUnmount() {
    // Clean up event listener
    const modal = this.$el.closest('.modal');
    if (modal) {
        modal.removeEventListener('show.bs.modal', this.refreshList);
    }
    },
  methods: {
    // Backend Integration Methods
    async refreshList() {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        if (this.vcCommunicator) {
          const response = await this.vcCommunicator.addressbookList();
          
          if (response.success || !response.errorCode) {
            this.entries = response.entries || [];
            this.$emit('entriesUpdated', this.entries);
          } else {
            throw new Error(response.message || this.$t('messages.failedToLoad'));
          }
        } else {
          // Fallback for testing
          console.warn('VcCommunicator not available, using initial entries');
          this.entries = [...this.initialEntries];
        }
      } catch (error) {
        console.error('Failed to refresh address book:', error);
        this.errorMessage = error.message || this.$t('messages.failedToLoad');
        this.$emit('error', error);
      } finally {
        this.isLoading = false;
      }
    },
    
    async saveEntry() {
      if (!this.isFormValid || this.isProcessing) return;
      
      this.isProcessing = true;
      this.errorMessage = '';
      
      try {
        const entryData = {
          name: this.formData.name.trim(),
          email: this.formData.email.trim()
        };
        
        if (this.vcCommunicator) {
          const endpoint = this.editingEntry ? 'addressbook/update' : 'addressbook/add';
          const response = await this.vcCommunicator.retrieveData(
            this.vcCommunicator.createUrl(endpoint),
            {
              ...entryData,
              ...(this.editingEntry && { id: this.editingEntry.id })
            }
          );
          
          if (response.success || !response.errorCode) {
            await this.refreshList();
            this.closeModal();
          } else {
            throw new Error(response.message || this.$t('messages.failedToSave'));
          }
        } else {
          // Fallback for testing
          if (this.editingEntry) {
            const index = this.entries.findIndex(e => e.id === this.editingEntry.id);
            if (index !== -1) {
              this.entries[index] = { ...this.entries[index], ...entryData };
            }
          } else {
            this.entries.push({
              id: Date.now(),
              ...entryData
            });
          }
          this.$emit('entriesUpdated', this.entries);
          this.closeModal();
        }
      } catch (error) {
        console.error('Failed to save entry:', error);
        this.errorMessage = error.message || this.$t('messages.failedToSave');
        this.$emit('error', error);
      } finally {
        this.isProcessing = false;
      }
    },
    
    async deleteEntry() {
      if (!this.deletingEntry || this.isProcessing) return;
      
      this.isProcessing = true;
      this.errorMessage = '';
      
      try {
        if (this.vcCommunicator) {
          const response = await this.vcCommunicator.retrieveData(
            this.vcCommunicator.createUrl('addressbook/delete'),
            { id: this.deletingEntry.id }
          );
          
          if (response.success || !response.errorCode) {
            await this.refreshList();
            this.deletingEntry = null;
          } else {
            throw new Error(response.message || this.$t('messages.failedToDelete'));
          }
        } else {
          // Fallback for testing
          this.entries = this.entries.filter(e => e.id !== this.deletingEntry.id);
          this.$emit('entriesUpdated', this.entries);
          this.deletingEntry = null;
        }
      } catch (error) {
        console.error('Failed to delete entry:', error);
        this.errorMessage = error.message || this.$t('messages.failedToDelete');
        this.$emit('error', error);
      } finally {
        this.isProcessing = false;
      }
    },
    
    // UI Methods
    editEntry(entry) {
      this.editingEntry = entry;
      this.formData = {
        name: entry.name || '',
        email: entry.email || ''
      };
    },
    
    confirmDelete(entry) {
      this.deletingEntry = entry;
    },
    
    closeModal() {
      this.showAddModal = false;
      this.editingEntry = null;
      this.resetForm();
    },
    
    resetForm() {
      this.formData = {
        name: '',
        email: ''
      };
    },
    
    isValidEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    }
  }
};
</script>

<style scoped>
.address-book-editor {
  max-width: 800px;
  margin: 0 auto;
}

.list-group-item {
  transition: background-color 0.2s ease;
}

.list-group-item:hover {
  background-color: var(--bs-gray-100);
}
</style>